import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from '../../../components/ui/Modal';
import Btn from '../../../components/ui/Btn';
import AgentDot from '../../../components/ui/AgentDot';
import Field, { TextInput, NativeSelect } from '../../../components/ui/Field';
import { useAuth } from '../../../context/AuthContext';
import { agentsService, friendlyError } from '../../../services/agentsService';
import { toastSuccess } from '../../../utils/toast';

export const CLONE_STAGES = ['Clonando comportamiento', 'Desplegando herramientas', 'Configurando entorno', 'Dejando todo listo'];
const STAGE_MS = 1100;           // cada etapa se ve al menos este tiempo, aunque la copia termine antes
const reduceMotion = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/** Un agente se puede clonar si es de la plataforma, no está archivado y es público o lo creó esta persona. */
export const clonable = (a, uid) => a.managed_by === 'platform' && a.status !== 'archived' && (a.visibility === 'public' || (uid && a.created_by === uid));

/**
 * Clonar un agente: pide el nombre y el proyecto, muestra una pantalla de progreso mientras se copia y, al terminar, abre el resumen
 * del agente nuevo para dar de alta lo que le falta (clave de OpenAI y canales; no se copian).
 * `source`: el agente a clonar (desde su ficha). Sin él, se elige de la lista `agents` (los públicos y los propios).
 */
export default function CloneAgentModal({ open, onClose, source = null, agents = [] }) {
  const navigate = useNavigate();
  const { currentUser } = useAuth() || {};
  const uid = currentUser?.id;
  const lockedProject = currentUser?.role === 'employee' ? currentUser.employeeProject : null;   // un empleado clona siempre en su proyecto interno
  const options = useMemo(() => agents.filter((a) => clonable(a, uid)).sort((x, y) => x.name.localeCompare(y.name, 'es')), [agents, uid]);
  const [sourceId, setSourceId] = useState('');
  const [name, setName] = useState('');
  const [projectId, setProjectId] = useState('');
  const [projects, setProjects] = useState([]);
  const [types, setTypes] = useState([]);
  const [mode, setMode] = useState('existing');         // existing | new: el clon vive en un proyecto que ya existe o en uno nuevo (id distinto)
  const [projectName, setProjectName] = useState('');
  const [projectNameEdited, setProjectNameEdited] = useState(false);
  const [projectDesc, setProjectDesc] = useState('');
  const [typeId, setTypeId] = useState('');
  const [useDraft, setUseDraft] = useState(false);
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState('');
  const [run, setRun] = useState(null);                 // null | { name, stage, done }
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  const src = source || options.find((a) => String(a.id) === sourceId) || null;
  const hasPublished = Boolean(src && src.published_version != null);

  useEffect(() => {
    if (!open) return;
    setTouched(false); setError(''); setUseDraft(false);
    const first = source || null;
    setSourceId(first ? String(first.id) : '');
    setName(first ? `Copia de ${first.name}`.slice(0, 120) : '');
    setProjectId(lockedProject ? String(lockedProject) : first ? String(first.project_id) : '');
    setMode('existing'); if (lockedProject) setProjectId(String(lockedProject)); setProjectName(''); setProjectNameEdited(false); setProjectDesc('');
    setTypeId(first?.agent_type_id != null ? String(first.agent_type_id) : '');
    agentsService.listProjects().then(setProjects).catch(() => {});
    agentsService.listProjectTypes().then((t) => { setTypes(t); setTypeId((cur) => cur || String((t.find((x) => /leads/i.test(x.name || '')) || t[0])?.id ?? '')); }).catch(() => {});
    // Solo al abrir.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const pick = (id) => {
    setSourceId(id);
    const a = options.find((x) => String(x.id) === id);
    if (a) { setName(`Copia de ${a.name}`.slice(0, 120)); setProjectId(String(lockedProject || a.project_id)); setUseDraft(false); if (a.agent_type_id != null) setTypeId(String(a.agent_type_id)); }
  };

  // Si nadie escribió el nombre del proyecto nuevo, se toma el del agente nuevo.
  useEffect(() => { if (mode === 'new' && !projectNameEdited) setProjectName(name); }, [name, mode, projectNameEdited]);

  const nameError = touched && (name.trim().length < 2 || name.trim().length > 120) ? 'Escribe un nombre de 2 a 120 caracteres.' : '';
  const sourceError = touched && !src ? 'Elige el agente que quieres clonar.' : '';
  const projectError = touched && mode === 'existing' && !projectId ? 'Elige el proyecto donde vivirá el clon.' : '';
  const projectNameError = touched && mode === 'new' && (projectName.trim().length < 3 || projectName.trim().length > 100) ? 'De 3 a 100 caracteres.' : '';
  const typeError = touched && mode === 'new' && !typeId ? 'Elige el tipo de agente.' : '';

  const submit = async (e) => {
    e?.preventDefault();
    setTouched(true);
    const projectOk = mode === 'existing' ? Boolean(projectId) : projectName.trim().length >= 3 && projectName.trim().length <= 100 && Boolean(typeId);
    if (!src || !projectOk || name.trim().length < 2 || name.trim().length > 120) return;
    setError('');
    const label = name.trim();
    setRun({ name: label, stage: 0, done: false });
    const started = Date.now();
    const timer = setInterval(() => { if (alive.current) setRun((r) => (r && !r.done && r.stage < CLONE_STAGES.length - 1 ? { ...r, stage: r.stage + 1 } : r)); }, reduceMotion() ? 400 : STAGE_MS);
    let createdProject = null;
    try {
      let target = Number(projectId);
      if (mode === 'new') {
        try { createdProject = await agentsService.createProjectQuick({ name: projectName, description: projectDesc, type: types.find((t) => String(t.id) === typeId) }); }
        catch (err) { throw new Error(`No se pudo crear el proyecto: ${friendlyError(err)}`); }
        target = createdProject.id;
      }
      const result = await agentsService.cloneAgent({ sourceId: src.id, projectId: target, name: label, useDraft: useDraft || !hasPublished });
      // Que se vean todas las etapas, aunque la copia haya sido instantánea.
      const minimum = (reduceMotion() ? 400 : STAGE_MS) * CLONE_STAGES.length;
      const wait = Math.max(0, minimum - (Date.now() - started));
      await new Promise((r) => setTimeout(r, wait));
      clearInterval(timer);
      if (!alive.current) return;
      setRun({ name: label, stage: CLONE_STAGES.length - 1, done: true });
      await new Promise((r) => setTimeout(r, 700));
      toastSuccess(`«${label}» clonado. Falta conectar su clave y sus canales`);
      onClose?.();
      navigate(`/agentes/${result.agent_id}`);
      if (alive.current) setRun(null);
    } catch (err) {
      clearInterval(timer);
      if (!alive.current) return;
      setRun(null);
      if (createdProject) {
        // El proyecto ya existe: se deja elegido para no duplicarlo al reintentar.
        setProjects((ps) => [...ps, createdProject]); setMode('existing'); setProjectId(String(createdProject.id));
        setError(`El proyecto «${createdProject.name}» se creó, pero el agente no: ${friendlyError(err)} Ya quedó elegido para que reintentes sin duplicarlo.`);
      } else setError(err.message?.startsWith('No se pudo crear el proyecto') ? err.message : friendlyError(err));
    }
  };

  return (
    <>
      <Modal open={open && !run} onClose={onClose} title="Clonar agente" width="max-w-xl"
        description="Crea un agente igual a otro, con otro nombre. La clave de OpenAI y los canales se conectan aparte, en el agente nuevo."
        footer={<><Btn variant="ghost" onClick={onClose}>Cancelar</Btn><Btn variant="primary" onClick={submit}>Clonar agente</Btn></>}>
        <form onSubmit={submit} className="space-y-4" noValidate>
          {source ? (
            <p className="text-[13.5px] text-fg-dim">Se clona <strong className="text-fg">{source.name}</strong>.</p>
          ) : (
            <Field label="Agente a clonar" error={sourceError} hint={options.length ? 'Aparecen los agentes públicos y los que tú creaste.' : 'Todavía no hay agentes públicos ni creados por ti. Marca uno como público en su ficha.'}>
              <NativeSelect value={sourceId} onChange={(e) => pick(e.target.value)}>
                <option value="">Elige un agente</option>
                {options.map((a) => <option key={a.id} value={a.id}>{a.name} · {a.project?.name || `Proyecto ${a.project_id}`}{a.visibility === 'public' ? ' · público' : ''}</option>)}
              </NativeSelect>
            </Field>
          )}
          <Field label="Nombre del agente nuevo" error={nameError}>
            <TextInput value={name} onChange={(e) => setName(e.target.value)} maxLength={120} data-autofocus />
          </Field>
          {!lockedProject && <fieldset className="space-y-3">
            <legend className="text-[13px] font-medium text-fg mb-1.5">Proyecto donde vivirá el clon</legend>
            <div role="radiogroup" aria-label="Proyecto del clon" className="flex rounded-lg border border-line p-0.5 bg-ink w-full sm:w-auto">
              {[['existing', 'Uno que ya existe'], ['new', 'Proyecto nuevo']].map(([k, l]) => (
                <button key={k} type="button" role="radio" aria-checked={mode === k} onClick={() => setMode(k)}
                  className={`flex-1 sm:flex-none h-9 px-3 rounded-md text-[13px] font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60 ${mode === k ? 'bg-raised text-fg' : 'text-fg-dim hover:text-fg'}`}>{l}</button>
              ))}
            </div>
            {mode === 'existing' ? (
              <Field label="Proyecto" error={projectError} hint="Puede ser el mismo del original o el de otro cliente.">
                <NativeSelect value={projectId} onChange={(e) => setProjectId(e.target.value)}>
                  <option value="">Elige el proyecto</option>
                  {[...projects].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es')).map((p) => <option key={p.id} value={p.id}>{p.name || `Proyecto ${p.id}`}</option>)}
                </NativeSelect>
              </Field>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Nombre del proyecto" error={projectNameError}>
                  <TextInput value={projectName} onChange={(e) => { setProjectName(e.target.value); setProjectNameEdited(true); }} maxLength={100} autoComplete="off" placeholder="Clínica Dental Sonrisa" />
                </Field>
                <Field label="Tipo de agente" error={typeError}>
                  <NativeSelect value={typeId} onChange={(e) => setTypeId(e.target.value)}>
                    <option value="">Elige un tipo…</option>
                    {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </NativeSelect>
                </Field>
                <Field label="Descripción" optional className="sm:col-span-2">
                  <TextInput value={projectDesc} onChange={(e) => setProjectDesc(e.target.value)} maxLength={150} autoComplete="off" placeholder="Qué hace el cliente, en una línea" />
                </Field>
                <p className="sm:col-span-2 text-[12.5px] leading-relaxed text-fg-faint">Se crea un proyecto nuevo, con su propio id, con los módulos de su tipo y Chats encendido. Después lo encuentras en Proyectos.</p>
              </div>
            )}
          </fieldset>}
          {src && hasPublished && (
            <fieldset className="rounded-xl ring-1 ring-line p-3">
              <legend className="px-1 text-[13px] font-medium text-fg">Qué versión copiar</legend>
              <label className="flex items-start gap-2 py-1 text-[13.5px] text-fg"><input type="radio" name="ver" checked={!useDraft} onChange={() => setUseDraft(false)} className="mt-1" /><span>La publicada (versión {src.published_version}) <span className="text-fg-faint">— lo que hoy responde</span></span></label>
              <label className="flex items-start gap-2 py-1 text-[13.5px] text-fg"><input type="radio" name="ver" checked={useDraft} onChange={() => setUseDraft(true)} className="mt-1" /><span>El borrador actual <span className="text-fg-faint">— con los cambios aún sin publicar</span></span></label>
            </fieldset>
          )}
          <div className="grid gap-3 sm:grid-cols-2 text-[13px]">
            <div className="rounded-xl bg-raised/50 p-3"><p className="font-semibold text-fg">Se copia</p><ul className="mt-1 list-disc pl-4 text-fg-dim space-y-0.5"><li>Instrucciones y personalidad</li><li>Comportamiento y datos previos</li><li>Herramientas conectadas (las propias, como copia)</li><li>Conocimiento propio del agente</li></ul></div>
            <div className="rounded-xl bg-raised/50 p-3"><p className="font-semibold text-fg">Se conecta aparte</p><ul className="mt-1 list-disc pl-4 text-fg-dim space-y-0.5"><li>Clave de OpenAI</li><li>Telegram, WhatsApp y chat web</li><li>Conectores de las herramientas</li><li>Historial y costos: no se copian</li></ul></div>
          </div>
          {error && <p role="alert" className="text-sm text-signal">{error}</p>}
          <button type="submit" className="sr-only" tabIndex={-1}>Clonar agente</button>
        </form>
      </Modal>
      {run && <CloneProgress run={run} />}
    </>
  );
}

/** Pantalla completa mientras se clona: una barra que avanza por las etapas. Anuncia cada etapa a los lectores de pantalla. */
function CloneProgress({ run }) {
  const total = CLONE_STAGES.length;
  const pct = run.done ? 100 : Math.round(((run.stage + 0.5) / total) * 100);
  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-ink p-6" role="alertdialog" aria-modal="true" aria-labelledby="clon-titulo" aria-describedby="clon-etapa">
      <div className="w-full max-w-md text-center">
        <div className="mx-auto w-fit"><AgentDot name={run.name} size={96} state={run.done ? 'happy' : 'thinking'} follow={false} /></div>
        <h2 id="clon-titulo" className="mt-6 font-display text-[24px] font-bold text-fg break-words">{run.done ? 'Listo' : `Clonando «${run.name}»`}</h2>
        <p id="clon-etapa" role="status" aria-live="polite" className="mt-2 min-h-[1.5rem] text-[15px] text-fg-dim">
          {run.done ? 'Abriendo el resumen del agente nuevo…' : `${CLONE_STAGES[run.stage]}…`}
        </p>
        <div role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} aria-label="Avance de la clonación" className="mt-6 h-2 rounded-full bg-raised overflow-hidden ring-1 ring-line">
          <div className="h-full rounded-full bg-live transition-[width] duration-700 ease-out motion-reduce:transition-none" style={{ width: `${pct}%` }} />
        </div>
        <ol className="mt-5 space-y-1.5 text-left text-[13px]">
          {CLONE_STAGES.map((s, i) => {
            const state = run.done || i < run.stage ? 'done' : i === run.stage ? 'now' : 'next';
            return (
              <li key={s} className={`flex items-center gap-2.5 ${state === 'next' ? 'text-fg-faint' : state === 'now' ? 'text-fg font-semibold' : 'text-fg-dim'}`}>
                <span aria-hidden className={`grid h-4 w-4 place-items-center rounded-full text-[10px] ${state === 'done' ? 'bg-live text-ink' : state === 'now' ? 'ring-2 ring-live' : 'ring-1 ring-line'}`}>{state === 'done' ? '✓' : ''}</span>
                {s}
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
}
